import { useState } from 'react';

export default function ContactForm({
  title = 'Udfyld kontaktformularen her',
  subtitle = 'Få en uforpligtende snak og et konkret tilbud på din bådtransport på vandet.'
}) {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    baad: '',
    fra_havn: '',
    til_havn: '',
    dato: '',
    beskrivelse: '',
    website_url: '' // Honeypot
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.navn.trim()) errs.navn = 'Venligst indtast dit navn';
    if (!formData.telefon.trim()) errs.telefon = 'Venligst indtast dit telefonnummer';
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din emailadresse';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Indtast venligst en gyldig emailadresse';
    }
    if (!formData.beskrivelse.trim()) errs.beskrivelse = 'Venligst beskriv din opgave';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const payload = {
        navn: formData.navn,
        telefon: formData.telefon,
        email: formData.email,
        baad_model: formData.baad || 'Ikke angivet',
        fra_havn: formData.fra_havn || 'Ikke angivet',
        til_havn: formData.til_havn || 'Ikke angivet',
        oensket_tidspunkt: formData.dato || 'Ikke angivet',
        beskrivelse: formData.beskrivelse,
        website_url: formData.website_url // Honeypot
      };

      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setStatus('success');
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          baad: '',
          fra_havn: '',
          til_havn: '',
          dato: '',
          beskrivelse: '',
          website_url: ''
        });
      } else {
        const errorData = await res.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(
          errorData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring direkte.'
        );
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
      setErrorMessage(
        'Kunne ikke sende formularen. Tjek din internetforbindelse eller ring direkte på +45 50 51 00 62.'
      );
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-8 lg:p-10">
      <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
          {title}
        </h3>
        <p className="text-sm text-slate-600 mt-1">{subtitle}</p>
      </div>

      {status === 'success' ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-6 text-center space-y-3 animate-in fade-in duration-300">
          <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto text-xl">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h4 className="text-lg font-bold text-emerald-900">Tak for din henvendelse!</h4>
          <p className="text-sm text-emerald-800">
            Jeg har modtaget din besked og vender tilbage til dig hurtigst muligt med en vurdering af transportopgaven.
          </p>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-500 transition-colors"
          >
            <span>Send endnu en besked</span>
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-4 sm:space-y-5">
          {/* Mandatory Honeypot for Anti-spam */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Hjemmesideadresse (lad feltet stå tomt)</label>
            <input
              id="website_url"
              type="text"
              name="website_url"
              tabIndex="-1"
              autoComplete="off"
              value={formData.website_url}
              onChange={handleChange}
            />
          </div>

          {status === 'error' && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-start gap-3">
              <i className="fa-solid fa-circle-exclamation text-red-500 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-semibold">Kunne ikke indsende</p>
                <p>{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Navn & Telefon */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="form-navn" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                Navn <span className="text-red-500">*</span>
              </label>
              <input
                id="form-navn"
                type="text"
                name="navn"
                required
                value={formData.navn}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className={`w-full px-4 py-2.5 rounded-lg border text-sm bg-slate-50/50 text-slate-900 transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                  errors.navn ? 'border-red-500 bg-red-50/20' : 'border-slate-300'
                }`}
              />
              {errors.navn && <p className="text-xs text-red-600 mt-1">{errors.navn}</p>}
            </div>

            <div>
              <label htmlFor="form-telefon" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                id="form-telefon"
                type="tel"
                name="telefon"
                required
                value={formData.telefon}
                onChange={handleChange}
                placeholder="F.eks. +45 50 51 00 62"
                className={`w-full px-4 py-2.5 rounded-lg border text-sm bg-slate-50/50 text-slate-900 transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                  errors.telefon ? 'border-red-500 bg-red-50/20' : 'border-slate-300'
                }`}
              />
              {errors.telefon && <p className="text-xs text-red-600 mt-1">{errors.telefon}</p>}
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="form-email" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Emailadresse <span className="text-red-500">*</span>
            </label>
            <input
              id="form-email"
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="din-email@adresse.dk"
              className={`w-full px-4 py-2.5 rounded-lg border text-sm bg-slate-50/50 text-slate-900 transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                errors.email ? 'border-red-500 bg-red-50/20' : 'border-slate-300'
              }`}
            />
            {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
          </div>

          {/* Transport detaljer (fra scrape-felterne: Hvilken båd, Hvor står båden, Hvor skal båden hen, Hvornår) */}
          <div className="pt-2 border-t border-slate-100">
            <span className="block text-xs font-bold text-cyan-800 uppercase tracking-wider mb-3">
              Oplysninger om bådtransporten (valgfrit)
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="form-baad" className="block text-xs font-medium text-slate-600 mb-1">
                  Hvilken båd er det? (Type / mærke / fod)
                </label>
                <input
                  id="form-baad"
                  type="text"
                  name="baad"
                  value={formData.baad}
                  onChange={handleChange}
                  placeholder="F.eks. 32 fods sejlbåd / motorbåd"
                  className="w-full px-3.5 py-2 rounded-lg border border-slate-300 text-sm bg-slate-50/50 text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label htmlFor="form-dato" className="block text-xs font-medium text-slate-600 mb-1">
                  Hvornår skal båden flyttes?
                </label>
                <input
                  id="form-dato"
                  type="text"
                  name="dato"
                  value={formData.dato}
                  onChange={handleChange}
                  placeholder="F.eks. Maj 2026 / snarest muligt"
                  className="w-full px-3.5 py-2 rounded-lg border border-slate-300 text-sm bg-slate-50/50 text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label htmlFor="form-fra" className="block text-xs font-medium text-slate-600 mb-1">
                  Hvor står / ligger båden nu?
                </label>
                <input
                  id="form-fra"
                  type="text"
                  name="fra_havn"
                  value={formData.fra_havn}
                  onChange={handleChange}
                  placeholder="F.eks. Aarhus Lystbådehavn"
                  className="w-full px-3.5 py-2 rounded-lg border border-slate-300 text-sm bg-slate-50/50 text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label htmlFor="form-til" className="block text-xs font-medium text-slate-600 mb-1">
                  Hvor skal båden hen?
                </label>
                <input
                  id="form-til"
                  type="text"
                  name="til_havn"
                  value={formData.til_havn}
                  onChange={handleChange}
                  placeholder="F.eks. Horsens Marina / ny havn"
                  className="w-full px-3.5 py-2 rounded-lg border border-slate-300 text-sm bg-slate-50/50 text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="form-beskrivelse" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Beskrivelse <span className="text-red-500">*</span>
            </label>
            <textarea
              id="form-beskrivelse"
              name="beskrivelse"
              rows={4}
              required
              value={formData.beskrivelse}
              onChange={handleChange}
              placeholder="Fortæl kort om opgaven, eventuelle ønsker til rute, tidsramme eller særlige forhold..."
              className={`w-full px-4 py-2.5 rounded-lg border text-sm bg-slate-50/50 text-slate-900 transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                errors.beskrivelse ? 'border-red-500 bg-red-50/20' : 'border-slate-300'
              }`}
            />
            {errors.beskrivelse && <p className="text-xs text-red-600 mt-1">{errors.beskrivelse}</p>}
          </div>

          {/* Submit button */}
          <div>
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin text-sm" aria-hidden="true" />
                  <span>Sender besked...</span>
                </>
              ) : (
                <>
                  <span>Send besked</span>
                  <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
